import { useEffect, useState, useRef, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { lightImpactVibrate, onReady, updateAppStatusBar, trackEvent, disableHardwareBack } from '../../utils/appBridge'
import BackButton from '../../components/ui-components/BackButton'
import BrowseCategoriesTabs from '../../components/peer-analyser/browse-categories/BrowseCategoriesTabs'
import BrowseCategoriesContent from '../../components/peer-analyser/browse-categories/BrowseCategoriesContent'
import { EventNames } from '../../types/misc.types'
import { fetchBrowseCategories } from '../../repository/peer-analyser'
import { buildBrowseCategoriesData } from '../../utils/buildBrowseCategoriesData'
import {
  getFundNatureFromCategory,
  getFundNatureFromTabId,
  getTabIdFromFundNature
} from '../../utils/peerAnalyserUtils'
import { BrowseTabData, FundNature, BrowseTabId } from '../../types/peer-analyser'
import { usePeerAnalyserStore } from '../../state-management/peerAnalyserStore'
import { Player } from '@lottiefiles/react-lottie-player'
import { loadingAnimationLottie } from '../../components/ui-components/loading/loadingAnimationData'
import { getTopPadding } from '../../utils/sessionStorage'

export default function BrowseCategoriesPage() {
  const navigate = useNavigate()
  const onReadyLock = useRef<boolean>(false)
  const contentScrollRef = useRef<HTMLDivElement>(null)
  const hasScrolledToCategory = useRef<boolean>(false)
  const setSelectedCategoryId = usePeerAnalyserStore((state) => state.setSelectedCategoryId)
  const selectedCategoryId = usePeerAnalyserStore((state) => state.selectedCategoryId)
  const fundNature = usePeerAnalyserStore((state) => state.fundNature)
  const setFundNature = usePeerAnalyserStore((state) => state.setFundNature)
  const {
    data: backendData,
    isLoading,
    isError,
    isSuccess
  } = useQuery({
    queryKey: ['browseCategories'],
    queryFn: fetchBrowseCategories,
    retry: 1,
    refetchOnWindowFocus: false
  })

  // Build data from API response - memoized to prevent rebuilding on every render
  const browseCategoriesDataTransformed: BrowseTabData[] = useMemo(() => {
    return backendData ? buildBrowseCategoriesData(backendData) : []
  }, [backendData])

  const [selectedTabId, setSelectedTabId] = useState<BrowseTabId | string>('')

  // Set initial selected tab when data is loaded - based on current fund nature
  useEffect(() => {
    if (browseCategoriesDataTransformed?.length > 0 && !selectedTabId) {
      const currentFundNature = fundNature || FundNature.EQUITY
      const tabIdFromFundNature = getTabIdFromFundNature(currentFundNature)

      // Try to find tab matching current fund nature, otherwise use first tab
      const matchingTab = browseCategoriesDataTransformed?.find((tab) => tab?.id === tabIdFromFundNature)
      const tabToSelect = matchingTab || browseCategoriesDataTransformed?.[0]

      if (tabToSelect?.id) {
        setSelectedTabId(tabToSelect.id)
        // Ensure fund nature is set correctly
        setFundNature(getFundNatureFromTabId(tabToSelect.id))
      }
    }
  }, [browseCategoriesDataTransformed, selectedTabId, fundNature, setFundNature])

  // Scroll content to top when tab changes
  useEffect(() => {
    if (contentScrollRef.current && selectedTabId) {
      contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [selectedTabId])

  useEffect(() => {
    updateAppStatusBar('FFFFFF', 'dark')
    disableHardwareBack()

    // Track page load
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'BrowseCategories',
        screenType: 'BrowseCategories'
      }
    })
  }, [])

  useEffect(() => {
    // Call onReady when component is mounted
    if (!onReadyLock.current && (isSuccess || isError)) {
      onReadyLock.current = true
      onReady()
    }
  }, [isSuccess, isError])

  const handleBack = () => {
    lightImpactVibrate()

    // Track back button click
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'BrowseCategories',
        buttonLabel: 'Back'
      }
    })

    navigate(-1)
  }

  const handleTabSelect = (tabId: string) => {
    setSelectedTabId(tabId)

    // Update fund nature based on selected tab
    const newFundNature = getFundNatureFromTabId(tabId)
    setFundNature(newFundNature)

    // Track tab selection
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'BrowseCategories',
        buttonLabel: tabId,
        eventId: tabId
      }
    })
  }

  const handleCategoryClick = (categoryId: string) => {
    if (!categoryId) return

    lightImpactVibrate()

    // Track category selection
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'BrowseCategories',
        buttonLabel: categoryId,
        eventId: categoryId
      }
    })

    // Set selected category and fund nature in store
    setSelectedCategoryId(categoryId)
    setFundNature(getFundNatureFromCategory(categoryId, backendData))

    // Navigate back to peer analyser
    navigate('/peer-analyser')
  }

  const selectedTab: BrowseTabData | undefined = browseCategoriesDataTransformed?.find(
    (tab) => tab?.id === selectedTabId
  )

  // Scroll to selected category only once when entering the page
  useEffect(() => {
    if (hasScrolledToCategory.current || !selectedCategoryId || !contentScrollRef.current || !selectedTab) return

    // Wait for DOM to render, then scroll to the selected category
    const timeoutId = setTimeout(() => {
      const categoryElement = contentScrollRef.current?.querySelector(
        `[data-category-id="${selectedCategoryId}"]`
      ) as HTMLElement

      if (categoryElement && contentScrollRef.current) {
        const containerRect = contentScrollRef.current.getBoundingClientRect()
        const elementRect = categoryElement.getBoundingClientRect()
        const scrollTop = contentScrollRef.current.scrollTop
        const elementTop = elementRect.top - containerRect.top + scrollTop

        // Scroll to center the element in the viewport
        const containerHeight = contentScrollRef.current.clientHeight
        const elementHeight = categoryElement.offsetHeight
        const scrollPosition = elementTop - containerHeight / 2 + elementHeight / 2

        contentScrollRef.current.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        })

        // Mark that we've scrolled, so it only happens once
        hasScrolledToCategory.current = true
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [selectedCategoryId, selectedTab])

  if (isError || !selectedTab || !browseCategoriesDataTransformed?.length) {
    return (
      <div className='h-full w-full flex flex-col bg-white'>
        <div
          style={{ height: `${getTopPadding() + 40}px` }}
          key='small'
          id='fullHeaderSmall'
          className='w-full flex z-[99]'
        >
          <div
            style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
            className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b border-white`}
          >
            <div className='flex z-1 pt-[2px]'>
              <BackButton size={24} onClick={handleBack} />
            </div>
            <div className='w-full flex items-center pl-5'>
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] text-[16px]'>Browse Categories</div>
            </div>
          </div>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <p className='text-gray-600 text-sm'>Error loading categories.</p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className='h-full w-full flex flex-col bg-white'>
        <div
          style={{ height: `${getTopPadding() + 40}px` }}
          key='small'
          id='fullHeaderSmall'
          className='w-full flex z-[99]'
        >
          <div
            style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
            className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b border-white`}
          >
            <div className='flex z-1 pt-[2px]'>
              <BackButton size={24} onClick={handleBack} />
            </div>
            <div className='w-full flex items-center pl-5'>
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] text-[16px]'>Browse Categories</div>
            </div>
          </div>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <Player autoplay loop src={loadingAnimationLottie} style={{ width: '42px', height: '42px' }} />
            <p className='text-gray-600 text-sm'>Loading categories...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full w-full flex flex-col bg-white'>
      <div
        style={{ height: `${getTopPadding() + 40}px` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99]'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b border-white`}
        >
          <div className='flex z-1 pt-[2px]'>
            <BackButton onClick={handleBack} />
          </div>
          <div className='w-full flex items-center pl-5'>
            <div className='max-w-[70%] animate-fade-150 truncate font-[500]'>Browse Categories</div>
          </div>
        </div>
      </div>
      {browseCategoriesDataTransformed && (
        <BrowseCategoriesTabs
          tabs={browseCategoriesDataTransformed}
          selectedTabId={selectedTabId}
          onTabSelect={handleTabSelect}
        />
      )}
      {selectedTab && (
        <BrowseCategoriesContent
          selectedTab={selectedTab}
          onCategoryClick={handleCategoryClick}
          contentScrollRef={contentScrollRef}
        />
      )}
    </div>
  )
}
